import React, { useState } from 'react';
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SignInManager from "../SignInManager";
import { useHasActiveSubscription } from "../../hooks/useSubscriptionStatus";
import { getApiEndpoint } from "../../config/tenantConfig";
import { currentAuthenticatedUser } from "../../services/auth/authService";

const drawerWidth = "auto";

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

/**
 * UnifiedAppBar - Single navigation bar for all app modes
 *
 * Always shows on the right:
 * - Test API button (getCurrentTime)
 * - SignInManager (login/logout)
 * - Settings button
 *
 * Left side changes based on mode:
 * - 'search': Camera, history, search controls
 * - 'admin': Admin panel tabs
 * - 'operator': Operator-specific controls
 *
 * @param {string} mode - 'search' | 'admin' | 'operator'
 * @param {ReactNode} leftContent - Mode-specific left-side content
 * @param {ReactNode} centerContent - Mode-specific center content (optional)
 * @param {string} title - Optional title (for admin/operator modes)
 * @param {Function} onSettingsOpen - Handler for opening settings
 * @param {Function} onSubscriptionOpen - Handler for opening subscription dialog
 * @param {Object} cognitoUser - Current authenticated user
 * @param {boolean} drawerOpen - Whether drawer is open (for search mode)
 */
export default function UnifiedAppBar({
  mode = 'search',
  leftContent,
  centerContent,
  title,
  onSettingsOpen,
  onSubscriptionOpen,
  cognitoUser,
  drawerOpen = false
}) {
  const isUserActive = useHasActiveSubscription();

  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [timeData, setTimeData] = useState(null);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState(null);

  const handleGetCurrentTime = async () => {
    setTimeDialogOpen(true);
    setTimeLoading(true);
    setTimeError(null);
    setTimeData(null);

    try {
      const apiEndpoint = getApiEndpoint('getCurrentTime');
      if (!apiEndpoint) {
        throw new Error('Get Current Time API not configured');
      }

      const user = await currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;

      const response = await fetch(`${apiEndpoint}/time`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setTimeData(data);
    } catch (error) {
      console.error('Error fetching current time:', error);
      setTimeError(error.message);
    } finally {
      setTimeLoading(false);
    }
  };

  const handleCloseTimeDialog = () => {
    setTimeDialogOpen(false);
    setTimeData(null);
    setTimeError(null);
  };

  return (
    <>
      <AppBar
        position={mode === 'search' ? 'fixed' : 'static'}
        open={drawerOpen}
        sx={{ bgcolor: '#2d3e50' }}
      >
        <Toolbar>
          {/* Left side - mode-specific content */}
          {title && (
            <Typography variant="h6" component="div" sx={{ mr: 2 }}>
              {title}
            </Typography>
          )}
          {leftContent}

          {/* Center content */}
          {centerContent}

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - always visible across all modes */}
          <IconButton
            aria-label="get current time"
            color="inherit"
            onClick={handleGetCurrentTime}
            disabled={!isUserActive}
            title="Get Current Time (Test API)"
          >
            <AccessTimeIcon />
          </IconButton>

          {onSubscriptionOpen && (
            <IconButton
              aria-label="subscription"
              color="inherit"
              onClick={onSubscriptionOpen}
              title={isUserActive ? "Subscription (Active)" : "Subscription"}
              sx={{
                color: isUserActive ? '#4caf50' : 'inherit', // Green if active
                '&:hover': {
                  color: isUserActive ? '#66bb6a' : 'inherit', // Lighter green on hover
                }
              }}
            >
              <CreditCardIcon />
            </IconButton>
          )}

          <SignInManager />

          <IconButton
            aria-label="settings"
            color="inherit"
            onClick={onSettingsOpen}
            disabled={!isUserActive}
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Test API Dialog */}
      <Dialog open={timeDialogOpen} onClose={handleCloseTimeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Current Time</DialogTitle>
        <DialogContent>
          {timeLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {timeError && (
            <Typography color="error" sx={{ p: 2 }}>
              Error: {timeError}
            </Typography>
          )}
          {timeData && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(timeData.currentTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Timestamp: {timeData.timestamp}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tenant ID: {timeData.tenantId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User ID: {timeData.userId}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTimeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
